/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import {action, observable, makeObservable} from 'mobx';
import {ToastNotification} from '@carbon/react';

const NOTIFICATION_TIMEOUT = 5000;
const MAX_VISIBLE_NOTIFICATIONS = 5;

type Notification = {
  id: string;
  title: string;
  subtitle?: string;
  date: number;
  isDismissable: boolean;
  kind: NonNullable<React.ComponentProps<typeof ToastNotification>['kind']>;
  hideNotification: () => void;
  isActionable?: boolean;
  actionButtonLabel?: string;
  onActionButtonClick?: () => void;
};

class Notifications {
  notifications: Notification[] = [];
  #notificationsQueue: Notification[] = [];
  #activeIntervalIds = new Map<string, ReturnType<typeof setInterval>>();

  constructor() {
    makeObservable(this, {
      displayNotification: action,
      hideNotification: action,
      notifications: observable,
      reset: action,
    });
  }

  displayNotification = (
    notification: Omit<Notification, 'date' | 'id' | 'hideNotification'>,
  ) => {
    const notificationId = `${Date.now()}${Math.random()}`;
    const hideNotification = () => {
      this.hideNotification(notificationId);
    };
    const newNotification: Notification = {
      ...notification,
      date: Date.now(),
      id: notificationId,
      hideNotification,
    };

    if (this.notifications.length >= MAX_VISIBLE_NOTIFICATIONS) {
      this.#enqueueNotification(newNotification);
    } else {
      if (newNotification.isDismissable) {
        this.#addAutoRemovalInterval(newNotification);
      }
      this.notifications.unshift(newNotification);
    }

    return newNotification.hideNotification;
  };

  #enqueueNotification = (notification: Notification) => {
    this.#notificationsQueue.unshift(notification);
  };

  #dequeueNotification = () => {
    return this.#notificationsQueue.shift();
  };

  #addAutoRemovalInterval = (notification: Notification) => {
    const delta = 100;
    let time = NOTIFICATION_TIMEOUT;

    const intervalId = setInterval(() => {
      if (document.hidden) {
        return;
      }

      time -= delta;

      if (time <= 0) {
        clearInterval(intervalId);
        this.#activeIntervalIds.delete(notification.id);
        notification.hideNotification();
      }
    }, delta);
    this.#activeIntervalIds.set(notification.id, intervalId);
  };

  hideNotification = (notificationId: string) => {
    const intervalId = this.#activeIntervalIds.get(notificationId);
    if (intervalId !== undefined) {
      clearInterval(intervalId);
      this.#activeIntervalIds.delete(notificationId);
    }

    const queuedNotification = this.#dequeueNotification();

    this.notifications = this.notifications.filter(
      ({id}) => id !== notificationId,
    );

    if (queuedNotification !== undefined) {
      this.#addAutoRemovalInterval(queuedNotification);
      this.notifications.unshift(queuedNotification);
    }
  };

  reset = () => {
    this.notifications = [];
    this.#notificationsQueue = [];
    this.#activeIntervalIds.forEach((intervalId) => clearInterval(intervalId));
    this.#activeIntervalIds.clear();
  };
}

const notificationsStore = new Notifications();

export {notificationsStore};
export type {Notification};
